import { Box, Chip, Stack } from '@mui/material';
import { DocumentType } from '@synergy/models';
import { FormattedMessage, useIntl } from 'react-intl';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router';
import {
    PaloConfirmationDialog,
    PaloDetailView,
    PaloHistory,
    PaloRecordMenu,
    PaloStep,
    PaloTabs,
} from '@synergy/components-palo';
import { PoolDocumentTitle } from '@synergy/components-next';
import { PoolFormattedMessage } from '@synergy/components';
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { SubPageHandle } from '@synergy/utils';

import * as ActivityActions from '../../../../common/actions/activity-actions';
import * as SupplierDetailActions from '../supplier-detail/store/supplier-detail-actions';
import * as SupplierDetailConditionsActions from '../supplier-detail/conditions/store/supplier-conditions-actions';
import * as SupplierDetailOverviewActions from '../supplier-detail/overview/store/supplier-detail-overview-actions';
import { DetailStream } from '../../../activity-sidebar/details/detail-stream';
import { NoteType } from '../../../../common/models/enums/note-type';
import { PaloAccountTransactions } from '../../../../common/components/palo/palo-account-transactions';
import {
    PaloDirtyDot,
    PaloDirtyProvider,
    PaloDirtyScope,
    usePaloSaveOnLeave,
} from '../../../../common/components/palo/palo-dirty-context';
import { PaloRecordDock } from '../../../../common/components/palo/palo-record-dock';
import { PaloRecordNotices } from '../../../../common/components/palo/palo-record-notices';
import { PaloSaveAction, paloDocumentPhase } from '../../../detail-header/palo-main-action';
import { PaloSupplierDocuments } from './palo-supplier-documents';
import { ROUTES } from '../../../sidebar-menu/navigation-drawer/routes';
import { SupplierAttributeMapping } from '../supplier-detail/attribute-mapping/supplier-attribute-mapping';
import { SupplierCategoryMapping } from '../supplier-detail/category-mapping/supplier-category-mapping';
import { SupplierDetailAddresses } from '../supplier-detail/addresses/supplier-detail-addresses';
import { SupplierDetailConditions } from '../supplier-detail/conditions/supplier-detail-conditions';
import { SupplierDetailDocuments } from '../supplier-detail/documents/supplier-detail-documents';
import { SupplierDetailExternalCustomerMapping } from '../supplier-detail/external-customer-mapping/supplier-detail-external-customer-mapping';
import { SupplierDetailOverview } from '../supplier-detail/overview/supplier-detail-overview';
import { SupplierDetailTabs } from '../supplier-detail/models/enum/supplier-detail-tabs';
import { SupplierEmployees } from '../supplier-detail/employees/supplier-employees';
import { SupplierUnitMapping } from '../supplier-detail/unit-mapping/supplier-unit-mapping';
import { paloSchemaErrors } from '../../../../common/components/palo/palo-schema-errors';
import { supplierConditionsSelector } from '../supplier-detail/conditions/store/supplier-conditions-selectors';
import { supplierDetailConditionsValidationSchema } from '../supplier-detail/conditions/supplier-detail-conditions-validation-schema';
import { supplierDetailOverviewFormDataSelector } from '../supplier-detail/overview/store/supplier-detail-overview-selectors';
import { documentsSelector as supplierDocumentsSelector } from '../supplier-detail/documents/store/supplier-detail-documents-selectors';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useRedirectAfterNewSave } from '../../components/use-redirect-after-new-save';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const PaloSupplierDetailInner = (): ReactElement => {
    const subPageRef = useRef<SubPageHandle>(null);
    const { id: supplierIdParam } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const intl = useIntl();
    const dispatch = useThunkDispatch();

    const {
        supplier,
        baseline,
        isValid: overviewIsValid,
    } = useAppSelector(state => state.contactsAndOrganizations.suppliers.supplierDetail.overview);
    const addressCount = useAppSelector(supplierDetailOverviewFormDataSelector).formValues.addresses?.length ?? 0;
    const conditions = useAppSelector(supplierConditionsSelector);
    const documentCount = useAppSelector(supplierDocumentsSelector).length;
    const isNew = supplierIdParam ? isNaN(+supplierIdParam) : false;
    const supplierName = isNew
        ? intl.formatMessage({ id: 'app.contactsAndOrganizations.supplier.new' })
        : supplier?.name ?? '';
    const isLocked = !!supplier?.isLocked;
    const hideProducts = !!supplier?.hideProducts;

    const [showDeleteWarning, setShowDeleteWarning] = useState(false);
    const [showHideProductsWarning, setShowHideProductsWarning] = useState(false);
    const [showLockWarning, setShowLockWarning] = useState(false);
    const [showUnlockWarning, setShowUnlockWarning] = useState(false);

    useRedirectAfterNewSave(ROUTES.contactsAndOrganizations.suppliers.detail, supplier?.id);
    usePaloSaveOnLeave(subPageRef.current?.submit);

    useEffect(() => {
        dispatch(ActivityActions.setDocumentType(DocumentType.Supplier));

        return () => {
            dispatch(SupplierDetailActions.resetSupplierDetails());
            dispatch(ActivityActions.resetDocumentType());
            dispatch(ActivityActions.resetActivities());
        };
    }, [dispatch]);

    useEffect(() => {
        if (supplier?.id) {
            dispatch(ActivityActions.fetchActivities(DocumentType.Supplier, supplier.id));
        }
    }, [supplier, dispatch]);

    useEffect(() => {
        if (!supplier?.id && supplierIdParam && !isNaN(+supplierIdParam)) {
            dispatch(SupplierDetailOverviewActions.fetchSupplierDetails(+supplierIdParam));
        }
    }, [dispatch, supplier?.id, supplierIdParam]);

    // Fetched with the record, not with its tab: the save refuses over the
    // required fields of the conditions, and until they are here it has
    // nothing to refuse over (#2212, R-28).
    useEffect(() => {
        if (supplier?.id && !conditions.detailConditions) {
            dispatch(SupplierDetailConditionsActions.fetchSupplierConditions(supplier.id));
        }
    }, [conditions.detailConditions, dispatch, supplier?.id]);

    // The tab reports itself valid until its form has been mounted, so the dot
    // beside it said nothing on a record nobody had opened the tab of.
    const conditionIsValid = useMemo(
        () =>
            conditions.isValid &&
            0 ===
                Object.keys(paloSchemaErrors(supplierDetailConditionsValidationSchema, conditions.detailConditions))
                    .length,
        [conditions.detailConditions, conditions.isValid]
    );

    const handleSave = async (): Promise<void> => {
        if (subPageRef.current) {
            await subPageRef.current.validate();
            await subPageRef.current.submit();
        }
        if (supplier?.id) {
            dispatch(ActivityActions.fetchActivities(DocumentType.Supplier, supplier.id));
        }
        dispatch(SupplierDetailActions.saveSupplierDetails());
    };

    const handleDelete = (): void => {
        setShowDeleteWarning(false);
        dispatch(SupplierDetailActions.deleteSupplier());
    };

    const handleToggleHideProducts = (): void => {
        setShowHideProductsWarning(false);
        dispatch(SupplierDetailActions.toggleHideProducts());
    };

    const handleToggleLock = (): void => {
        setShowLockWarning(false);
        setShowUnlockWarning(false);
        dispatch(SupplierDetailActions.toggleLockSupplier());
    };

    const detailBase = ROUTES.contactsAndOrganizations.suppliers.detail.replace(
        ':id',
        String(supplier?.id ?? supplierIdParam)
    );
    const activeSegment = location.pathname.split('/').pop() ?? '';
    const activeTab = (Object.values(SupplierDetailTabs) as string[]).includes(activeSegment)
        ? (activeSegment as SupplierDetailTabs)
        : SupplierDetailTabs.Overview;

    // Reads the mark of the tab it labels — the point of the marker is to say
    // that something is waiting on a tab nobody is looking at (#2159).
    const tabLabel = (tab: string, label: ReactElement, valid = true): ReactElement => (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            {label}
            {!valid && <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'error.main' }} />}
            <PaloDirtyDot scope={tab} />
        </Box>
    );

    // A-1: locking, hiding and deleting belong in the "⋯" menu, not beside
    // "Speichern" — two switches and a red button there is a slip waiting to
    // happen. A supplier that is not saved yet has none of them.
    const actions = (
        <>
            {!isNew && (
                <PaloRecordMenu
                    label={intl.formatMessage({ id: 'app.common.moreActions' })}
                    actions={[
                        {
                            label: (
                                <FormattedMessage
                                    id={
                                        hideProducts
                                            ? 'app.contactsAndOrganizations.supplier.products.show'
                                            : 'app.contactsAndOrganizations.supplier.products.hideproducts'
                                    }
                                />
                            ),
                            onClick: () =>
                                hideProducts ? handleToggleHideProducts() : setShowHideProductsWarning(true),
                        },
                        {
                            label: <FormattedMessage id={isLocked ? 'app.label.unlock' : 'app.label.lock'} />,
                            onClick: () => (isLocked ? setShowUnlockWarning(true) : setShowLockWarning(true)),
                        },
                        {
                            label: <FormattedMessage id="app.common.delete" />,
                            danger: true,
                            onClick: () => setShowDeleteWarning(true),
                        },
                    ]}
                />
            )}
            <PaloSaveAction
                phase={paloDocumentPhase(undefined, { saved: !isNew })}
                comparesToServer={true}
                onSave={handleSave}
            />
        </>
    );

    const tabSteps: PaloStep[] = [
        [SupplierDetailTabs.Overview, 'details', overviewIsValid, false],
        [SupplierDetailTabs.Addresses, 'addresses', true, false],
        [SupplierDetailTabs.Employees, 'employees', true, true],
        [SupplierDetailTabs.Documents, 'documents', true, true],
        [SupplierDetailTabs.Conditions, 'conditions', conditionIsValid, true],
        [SupplierDetailTabs.Categories, 'categories', true, true],
        [SupplierDetailTabs.Units, 'units', true, true],
        [SupplierDetailTabs.Attributes, 'attributes', true, true],
        [SupplierDetailTabs.Transactions, 'transactions', true, true],
        [SupplierDetailTabs.ExternalCustomerMapping, 'externalCustomerMapping', true, true],
    ].map(([tab, key, valid, needsId]) => ({
        label: tabLabel(
            tab as string,
            <>
                <FormattedMessage id={`app.contactsAndOrganizations.detail.tab.${key}`} />
                {'addresses' === key && addressCount > 0 && (
                    <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400, ml: '5px' }}>
                        {addressCount}
                    </Box>
                )}
            </>,
            valid as boolean
        ),
        state: activeTab === tab ? 'now' : 'todo',
        disabled: (needsId as boolean) && !supplier?.id,
        onClick: () => navigate(`${detailBase}/${tab}`),
    }));

    const tabs = <PaloTabs steps={tabSteps} />;

    return (
        <>
            <PoolDocumentTitle titleTranslationId={'app.common.suppliers'} name={supplierName} />
            <PaloDetailView
                themeContent={false}
                breadcrumbs={[
                    {
                        label: 'Lieferanten',
                        onClick: () => navigate(ROUTES.contactsAndOrganizations.suppliers.list),
                    },
                ]}
                title={supplierName}
                status={
                    // Both states used to be readable from the switches in the
                    // header. With those gone (A-1), the title has to say it.
                    isLocked || hideProducts ? (
                        <Stack direction="row" spacing={0.5}>
                            {isLocked && (
                                <Chip size="small" color="error" label={<FormattedMessage id="app.common.locked" />} />
                            )}
                            {hideProducts && (
                                <Chip
                                    size="small"
                                    color="warning"
                                    label={
                                        <FormattedMessage id="app.contactsAndOrganizations.supplier.products.hidden" />
                                    }
                                />
                            )}
                        </Stack>
                    ) : undefined
                }
                actions={actions}
                alert={<PaloRecordNotices noteType={NoteType.Suppliers} entityId={supplier?.id} />}
                tabs={tabs}
                dock={
                    <PaloRecordDock
                        count={documentCount}
                        attachments={supplier?.id ? <PaloSupplierDocuments /> : undefined}
                    />
                }
                history={
                    <PaloHistory>
                        <DetailStream documentType={DocumentType.Supplier} documentId={supplier?.id} />
                    </PaloHistory>
                }
            >
                <Routes>
                    <Route
                        path={SupplierDetailTabs.Overview}
                        element={
                            <PaloDirtyScope name={SupplierDetailTabs.Overview}>
                                <SupplierDetailOverview ref={subPageRef} />
                            </PaloDirtyScope>
                        }
                    />
                    <Route
                        path={SupplierDetailTabs.Addresses}
                        element={
                            <PaloDirtyScope name={SupplierDetailTabs.Addresses}>
                                <SupplierDetailAddresses ref={subPageRef} />
                            </PaloDirtyScope>
                        }
                    />
                    <Route path={SupplierDetailTabs.Employees} element={<SupplierEmployees />} />
                    <Route path={SupplierDetailTabs.Documents} element={<SupplierDetailDocuments />} />
                    <Route
                        path={SupplierDetailTabs.Conditions}
                        element={
                            <PaloDirtyScope name={SupplierDetailTabs.Conditions}>
                                <SupplierDetailConditions ref={subPageRef} />
                            </PaloDirtyScope>
                        }
                    />
                    <Route path={SupplierDetailTabs.Categories} element={<SupplierCategoryMapping />} />
                    <Route path={SupplierDetailTabs.Units} element={<SupplierUnitMapping />} />
                    <Route path={SupplierDetailTabs.Attributes} element={<SupplierAttributeMapping />} />
                    <Route
                        path={SupplierDetailTabs.Transactions}
                        element={
                            supplier?.id ? <PaloAccountTransactions accountId={supplier.id} side="supplier" /> : null
                        }
                    />
                    <Route
                        path={SupplierDetailTabs.ExternalCustomerMapping}
                        element={
                            <PaloDirtyScope name={SupplierDetailTabs.ExternalCustomerMapping}>
                                <SupplierDetailExternalCustomerMapping ref={subPageRef} />
                            </PaloDirtyScope>
                        }
                    />
                    <Route
                        path="*"
                        element={<Navigate to={{ pathname: SupplierDetailTabs.Overview }} replace={true} />}
                    />
                </Routes>
            </PaloDetailView>

            <PaloConfirmationDialog
                isVisible={showDeleteWarning}
                title={<FormattedMessage id="app.contactsAndOrganizations.supplier.delete" />}
                action={<FormattedMessage id="app.common.delete" />}
                onCancel={() => setShowDeleteWarning(false)}
                onConfirm={handleDelete}
            >
                <PoolFormattedMessage
                    id="app.contactsAndOrganizations.supplier.deleteWarning"
                    values={{ name: supplierName }}
                />
            </PaloConfirmationDialog>
            <PaloConfirmationDialog
                isVisible={showHideProductsWarning}
                title={<FormattedMessage id="app.contactsAndOrganizations.supplier.products.hideproducts" />}
                action={<FormattedMessage id="app.contactsAndOrganizations.supplier.products.hide" />}
                onCancel={() => setShowHideProductsWarning(false)}
                onConfirm={handleToggleHideProducts}
            >
                <PoolFormattedMessage id="app.contactsAndOrganizations.supplier.products.hideproductsText" />
            </PaloConfirmationDialog>
            <PaloConfirmationDialog
                isVisible={showLockWarning}
                title={<FormattedMessage id="app.contactsAndOrganizations.supplier.lockSupplier.modal.title" />}
                action={<FormattedMessage id="app.label.lock" />}
                onCancel={() => setShowLockWarning(false)}
                onConfirm={handleToggleLock}
            >
                <PoolFormattedMessage
                    id="app.contactsAndOrganizations.supplier.lockSupplier.modal.text"
                    values={{ name: supplierName }}
                />
            </PaloConfirmationDialog>
            <PaloConfirmationDialog
                isVisible={showUnlockWarning}
                title={<FormattedMessage id="app.contactsAndOrganizations.supplier.unlockSupplier.modal.title" />}
                action={<FormattedMessage id="app.label.unlock" />}
                onCancel={() => setShowUnlockWarning(false)}
                onConfirm={handleToggleLock}
            >
                <PoolFormattedMessage
                    id="app.contactsAndOrganizations.supplier.unlockSupplier.modal.text"
                    values={{ name: supplierName }}
                />
            </PaloConfirmationDialog>
        </>
    );
};

export const PaloSupplierDetail = (): ReactElement => {
    const { id } = useParams<{ id: string }>();
    // A fresh document from the server is the one moment at which every part
    // of the mask, mounted or not, is provably saved.
    const loadedDocument = useAppSelector(
        state => state.contactsAndOrganizations.suppliers.supplierDetail.overview.baseline
    );

    return (
        <PaloDirtyProvider
            basePath={ROUTES.contactsAndOrganizations.suppliers.detail.replace(':id', String(id))}
            loadedDocument={loadedDocument}
        >
            <PaloSupplierDetailInner />
        </PaloDirtyProvider>
    );
};
